import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { FAQSchema, BreadcrumbSchema } from '../../components/seo/SchemaMarkup';
import { Breadcrumbs } from '../../components/seo/Breadcrumbs';
import { LatestArticles } from '../../components/blog/LatestArticles';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Badge } from '../../components/ui/badge';
import { Cake, Sparkles, Music, Crown, ArrowRight } from 'lucide-react';

export default function BirthdayPartyVenueJamshedpur() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "What birthday party packages does Scratch Unleashed offer?",
      answer: "We offer three main birthday packages: Bronze (standard table for 6-8 people with bottle service), Silver (VIP booth for 10-12 with premium spirits and decorations), and Gold (exclusive lounge area for 15-20 with DJ shoutout, dedicated host, and custom arrangements). All packages can be customized based on your needs."
    },
    {
      question: "What is included in a birthday party booking?",
      answer: "All birthday packages include reserved seating, bottle service, complimentary entry for your group, a dedicated host, birthday decorations (balloons, table setup), and a celebratory cake cutting arrangement. Premium packages include DJ announcements, custom lighting, and photography services."
    },
    {
      question: "How far in advance should I book for a birthday party?",
      answer: "We recommend booking at least 2 weeks in advance for regular weekends and 3-4 weeks for holiday weekends or special event nights. This ensures we can accommodate your group size and any special requests. Last-minute bookings are subject to availability."
    },
    {
      question: "Can I bring my own birthday cake?",
      answer: "Absolutely! You're welcome to bring your own birthday cake. We provide complimentary cake cutting service, plates, and cutlery. If you need cake delivery assistance or want to order through our partner vendors, our events team can help coordinate."
    },
    {
      question: "What is the minimum age for birthday celebrations at Scratch Unleashed?",
      answer: "All guests must be 21+ as we are a licensed nightclub. We strictly verify age at entry with government-issued photo ID. This policy applies to all birthday party bookings and cannot be waived."
    },
    {
      question: "Can you accommodate surprise birthday parties?",
      answer: "Yes! We love helping create surprise celebrations. Our team can coordinate with you to ensure the birthday person is surprised while ensuring smooth entry and setup. We've successfully executed dozens of surprise parties with perfect timing."
    }
  ];

  const breadcrumbItems = [
    { name: 'Birthday Party Venue in Jamshedpur', url: null }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <SEOHead
        title="Birthday Party Venue in Jamshedpur | Celebrate at Scratch Unleashed"
        description="Best birthday party venue in Jamshedpur! Scratch Unleashed offers VIP packages, DJ nights, bottle service & custom celebrations. Make your birthday unforgettable!"
        keywords="birthday party venue Jamshedpur, birthday celebration Jamshedpur, party hall Jamshedpur, birthday party packages Jamshedpur, nightclub birthday party, VIP birthday Jamshedpur"
        canonical="https://scratchjsr.com/birthday-party-venue-jamshedpur"
      />
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://scratchjsr.com/' },
        { name: 'Birthday Party Venue in Jamshedpur', url: 'https://scratchjsr.com/birthday-party-venue-jamshedpur' }
      ]} />

      <div className="h-16"></div>

      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Hero Section */}
        <div className="mb-12">
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl mb-4">
            BIRTHDAY PARTY VENUE IN JAMSHEDPUR
          </h1>
          <p className="text-xl text-[var(--text-dim)] mb-6">
            Celebrate Your Special Day In Style
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
              Book Your Party
              <ArrowRight size={16} className="ml-2" />
            </Button>
            <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/#vip')}>
              View VIP Packages
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">The Best Birthday Party Venue in Jamshedpur</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Looking for a <strong>birthday party venue in Jamshedpur</strong> that will make your celebration truly memorable? Scratch Unleashed offers an experience that goes far beyond traditional party halls and restaurants. We've become Jamshedpur's premier destination for unforgettable birthday celebrations, combining the energy of a world-class nightclub with personalized service that makes your special day extraordinary.
                </p>
                <p>
                  Whether you're planning an intimate gathering for close friends or a large-scale celebration with dozens of guests, Scratch Unleashed provides the perfect setting. Our venue transforms into your personal celebration space, complete with dedicated VIP areas, premium bottle service, live DJ entertainment, and an atmosphere that's designed to create memories that last a lifetime.
                </p>
              </div>
            </section>

            {/* Why Choose Us */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Why Choose Scratch Unleashed for Your Birthday</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  What makes Scratch Unleashed the <strong>best birthday party venue in Jamshedpur</strong>? It's the combination of venue quality, service excellence, and the unique nightclub atmosphere that you simply can't replicate at a traditional banquet hall or restaurant.
                </p>
                <p>
                  First, there's the venue itself. Our state-of-the-art sound system, dynamic lighting design, and premium interior create an environment that feels special from the moment you walk in. When your guests arrive, they're not just entering another restaurant—they're stepping into Jamshedpur's most exciting nightlife destination, and it's all dedicated to celebrating you.
                </p>
                <p>
                  Second, our service model is built around making you feel like a VIP. From the moment you contact us to the end of your celebration, you'll have a dedicated events coordinator who handles every detail. Want specific songs played? Need help coordinating a surprise? Have dietary requirements for your cake? We handle it all, letting you focus entirely on enjoying your special night.
                </p>
                <p>
                  Third, the energy. A birthday celebration at Scratch Unleashed isn't a sit-down dinner—it's a party. The music is pumping, the crowd is electric, and the dance floor is calling. Your friends won't be checking their watches wondering when they can leave; they'll be asking if we can stay open later.
                </p>
              </div>
            </section>

            {/* Package Options */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Birthday Party Packages</h2>
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--text)]">Bronze Package</h3>
                    <Badge className="text-xs">Popular Choice</Badge>
                  </div>
                  <p className="text-[var(--text-dim)] mb-3">
                    Perfect for intimate celebrations with your closest friends. Includes a reserved standard table for 6-8 people, one premium bottle with mixers, complimentary entry for your group, birthday decorations, and cake cutting service. Starting from ₹15,000.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--text)]">Silver Package</h3>
                    <Badge variant="outline" className="text-xs border-[var(--accent-gold)] text-[var(--accent-gold)]">Recommended</Badge>
                  </div>
                  <p className="text-[var(--text-dim)] mb-3">
                    Step up to VIP status with a private booth for 10-12 people. Includes two premium bottles, priority entry, enhanced decorations (LED balloons, custom signage), DJ birthday announcement, dedicated server, and complimentary photography (50 photos). Starting from ₹30,000.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-[var(--text)]">Gold Package</h3>
                    <Badge className="text-xs bg-[var(--accent-gold)] text-black">Premium</Badge>
                  </div>
                  <p className="text-[var(--text-dim)] mb-3">
                    The ultimate birthday experience. Exclusive lounge area for 15-20 people, three premium bottles of your choice, VIP fast-track entry, custom lighting and decor, personalized DJ setlist, dedicated birthday host, professional photography package (200+ photos + video highlights), and special cake arrangement. Starting from ₹50,000.
                  </p>
                </div>
              </div>
            </section>

            {/* What's Included */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">What Makes Our Birthday Celebrations Special</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Every <strong>birthday party at Scratch Unleashed</strong> includes elements designed to make the celebration feel truly special. We're open for public celebrations Friday through Sunday from 6 PM onwards, and we're also available for exclusive private birthday parties Monday through Thursday. Here's what you can expect:
                </p>
                <p>
                  <strong>Personalized Service:</strong> Your dedicated host greets you upon arrival, escorts you to your reserved area, and ensures everything runs smoothly throughout the night. Need anything? They're always nearby, ready to help.
                </p>
                <p>
                  <strong>Premium Bottle Service:</strong> All packages include bottle service with premium spirits and mixers. Your server keeps your table stocked, ensures glasses are never empty, and handles all drink preparation—you focus on celebrating.
                </p>
                <p>
                  <strong>DJ Integration:</strong> Our DJs are part of the celebration. Whether it's a special birthday announcement over the mic, playing your favorite songs, or timing the music for your cake cutting moment, they're tuned into your event.
                </p>
                <p>
                  <strong>Instagram-Worthy Moments:</strong> From our atmospheric lighting to custom birthday setups, every corner of Scratch Unleashed is designed to photograph beautifully. Your social media will be filled with stunning shots that capture the night's energy.
                </p>
                <p>
                  <strong>Flexibility:</strong> While we have standard packages, we understand every celebration is unique. Want to combine elements from different packages? Need accommodations for a larger group? Have a specific theme in mind? Our events team works with you to create a custom package that fits your vision and budget.
                </p>
              </div>
            </section>

            {/* Planning Tips */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Planning Your Perfect Birthday Party</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Booking your <strong>birthday celebration at Scratch Unleashed</strong> is straightforward, but a few planning tips will help ensure everything is perfect:
                </p>
                <p>
                  <strong>Book Early:</strong> Our weekend slots (Friday-Sunday) fill up quickly, especially for larger groups and premium packages. Booking 2-4 weeks in advance gives us time to accommodate special requests and ensures your preferred date and area are available. For weekday private parties, we recommend booking at least 1-2 weeks ahead.
                </p>
                <p>
                  <strong>Coordinate with Your Group:</strong> Once you've booked, share the details with your guests. We can provide a guest list system for larger parties to streamline entry. Make sure everyone knows the 21+ age requirement and dress code.
                </p>
                <p>
                  <strong>Communicate Special Requests:</strong> Planning a surprise? Have specific songs that must be played? Need help coordinating cake delivery? The earlier you communicate these details to your events coordinator, the smoother everything will be.
                </p>
                <p>
                  <strong>Plan Your Timing:</strong> For weekend celebrations, arriving around 7-8 PM works best—early enough to settle in before peak energy, but late enough that the nightclub atmosphere is fully alive. For weekday private parties, timing is flexible based on your group's needs.
                </p>
              </div>
            </section>

            {/* Call to Action */}
            <section className="bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8">
              <h2 className="font-bebas text-3xl mb-4">Ready to Celebrate Your Birthday?</h2>
              <p className="text-[var(--text-dim)] mb-6">
                Don't settle for an ordinary birthday celebration. Make it extraordinary at Scratch Unleashed, where every detail is designed to create an unforgettable night. Contact our events team today to discuss your birthday package.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
                  Book Your Birthday Party
                </Button>
                <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/vip-table-booking-jamshedpur')}>
                  Explore VIP Options
                </Button>
              </div>
            </section>

            {/* FAQ Section */}
            <section>
              <h2 className="font-bebas text-3xl mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
                    <AccordionTrigger className="hover:no-underline text-left">
                      <span className="font-semibold">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Package Highlights */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Package Highlights</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Crown size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">VIP Treatment</p>
                    <p className="text-sm text-[var(--text-dim)]">Dedicated host & priority service</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Music size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">DJ Integration</p>
                    <p className="text-sm text-[var(--text-dim)]">Shoutouts & custom playlist</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Sparkles size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Premium Setup</p>
                    <p className="text-sm text-[var(--text-dim)]">Decorations & atmosphere</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Cake size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Cake Service</p>
                    <p className="text-sm text-[var(--text-dim)]">Complimentary cutting arrangement</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Starting Prices */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Starting From</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Bronze Package</span>
                  <span className="font-semibold text-[var(--accent-gold)]">₹15,000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Silver Package</span>
                  <span className="font-semibold text-[var(--accent-gold)]">₹30,000</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Gold Package</span>
                  <span className="font-semibold text-[var(--accent-gold)]">₹50,000</span>
                </div>
                <p className="text-xs text-[var(--text-muted)] pt-2 border-t border-[var(--border-custom)]">
                  Custom packages available. Contact us for pricing.
                </p>
              </div>
            </Card>


            {/* Latest Articles */}
            <LatestArticles maxPosts={3} />

            {/* Related Pages */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Explore More</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/night-club-in-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → Night Club in Jamshedpur
                </button>
                <button
                  onClick={() => navigate('/dj-night-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → DJ Nights
                </button>
                <button
                  onClick={() => navigate('/vip-table-booking-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → VIP Table Booking
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
